// ChildrenBreakdownTable.jsx
//
// Таблиця садочків одного району (або всього міста) для вкладки "Діти в
// садочках" — аналог KindergartenBreakdownTable.jsx для "Заявки на вступ".
// Розгортання рядка садочка показує ChildrenKindergartenDetail (розподіл за
// статтю/пільгою/труднощами).
import React, { useState, useMemo } from 'react';
import { useDragScroll } from './hooks/useDragScroll';
import { useIsMobile } from './hooks/useIsMobile';
import { matchesNumericFilter, describeFilter, validateFilter } from './utils/parseNumericFilter';
import ChildrenKindergartenDetail from './ChildrenKindergartenDetail';
import MobileSheet from './MobileSheet';

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig.key !== column) {
    return <span style={{ color: '#aaa', fontSize: '0.7rem', marginLeft: 4 }}>↕</span>;
  }
  return <span style={{ color: '#0069ff', fontSize: '0.7rem', marginLeft: 4 }}>
    {sortConfig.direction === 'asc' ? '↑' : '↓'}
  </span>;
};

const numCols = [
  { key: 'total',    label: 'Разом' },
  { key: 'total3_6', label: 'Разом 3-6' },
  { key: 'age0_1',   label: '0-1 рік' },
  { key: 'age1_2',   label: '1-2 роки' },
  { key: 'age2_3',   label: '2-3 роки' },
  { key: 'age3_4',   label: '3-4 роки' },
  { key: 'age4_5',   label: '4-5 років' },
  { key: 'age5_6',   label: '5-6 років' },
  { key: 'age6_7',   label: '6-7 років' },
  { key: 'age7_8',   label: '7-8 років' },
  { key: 'age8_9',   label: '8-9 років' },
];

const filterInputStyle = {
  width: '100%',
  border: '1px solid #c8d8ff',
  borderRadius: 4,
  padding: '4px 8px',
  fontSize: '0.8rem',
  background: '#fff',
  color: '#333',
  boxSizing: 'border-box',
};

const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const emptyFilters = {
  kgnumber: '',
  district: '',
  age0_1: '', age1_2: '', age2_3: '', age3_4: '', age4_5: '',
  age5_6: '', age6_7: '', age7_8: '', age8_9: '', total: '', total3_6: '',
};

const ChildrenBreakdownTable = ({ rows, dataParams, disabilities, loading, showCheckboxes = false, showDistrict = false, capHeight = true, scopeLabel = 'районі' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [columnFilters, setColumnFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [openDetailId, setOpenDetailId] = useState(null);

  const isMobile = useIsMobile();
  const { ref: scrollRef, listeners: dragListeners } = useDragScroll();

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const handleColumnFilter = (col, value) => {
    setColumnFilters(prev => ({ ...prev, [col]: value }));
  };

  const clearFilters = () => setColumnFilters(emptyFilters);

  const processedData = useMemo(() => {
    let list = [...rows];

    if (columnFilters.kgnumber)
      list = list.filter(r => String(r.kgnumber).toLowerCase().includes(columnFilters.kgnumber.toLowerCase()));
    if (showDistrict && columnFilters.district)
      list = list.filter(r => String(r.district).toLowerCase().includes(columnFilters.district.toLowerCase()));

    numCols.forEach(({ key }) => {
      if (columnFilters[key])
        list = list.filter(r => matchesNumericFilter(r[key], columnFilters[key]));
    });

    if (sortConfig.key) {
      list.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        const cmp = typeof aVal === 'number'
          ? aVal - bVal
          : String(aVal).localeCompare(String(bVal), 'uk');
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }

    return list;
  }, [rows, columnFilters, sortConfig]);

  const filteredTotals = useMemo(() => {
    if (!processedData.length) return null;
    const t = { total: 0, total3_6: 0, age0_1: 0, age1_2: 0, age2_3: 0, age3_4: 0, age4_5: 0, age5_6: 0, age6_7: 0, age7_8: 0, age8_9: 0 };
    processedData.forEach(r => {
      t.total += r.total; t.total3_6 += r.total3_6;
      t.age0_1 += r.age0_1; t.age1_2 += r.age1_2; t.age2_3 += r.age2_3; t.age3_4 += r.age3_4;
      t.age4_5 += r.age4_5; t.age5_6 += r.age5_6; t.age6_7 += r.age6_7; t.age7_8 += r.age7_8; t.age8_9 += r.age8_9;
    });
    return t;
  }, [processedData]);

  const hasActiveFilters = Object.values(columnFilters).some(v => v !== '');

  const openDetailRow = useMemo(
    () => processedData.find(r => r.id === openDetailId) || null,
    [processedData, openDetailId]
  );

  if (!rows.length) {
    return (
      <div className="text-muted" style={{ fontSize: '0.85rem', padding: '14px 16px' }}>
        {loading ? 'Завантаження садочків…' : 'Немає садочків за обраними параметрами'}
      </div>
    );
  }

  return (
    <div style={{ padding: '12px 16px' }}>
      <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
        <div style={{ fontSize: '0.82rem', color: '#666' }}>
          Садочків у {scopeLabel}: {rows.length}
          {processedData.length < rows.length && ` · показано ${processedData.length}`}
        </div>
        <div className="d-flex gap-2 align-items-center">
          {hasActiveFilters && (
            <span className="badge bg-primary" style={{ fontSize: '0.72rem' }}>
              Фільтри активні: {Object.values(columnFilters).filter(v => v !== '').length}
            </span>
          )}
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
            onClick={() => setShowFilters(p => !p)}
          >
            {showFilters ? '✕ Сховати фільтри' : '⚙ Фільтри по колонках'}
          </button>
          {hasActiveFilters && (
            <button
              className="btn btn-sm btn-outline-danger"
              style={{ fontSize: '0.75rem' }}
              onClick={clearFilters}
            >
              Скинути
            </button>
          )}
        </div>
      </div>

      {showFilters && (
        <div style={{ background: '#f0f4ff', border: '1px solid #c8d8ff', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '0.76rem', color: '#555' }}>
          💡 Числові поля підтримують: <code>{'>' + '10'}</code>, <code>{'<' + '50'}</code>, <code>{'5-20'}</code>, <code>{'!=' + '5'}</code>, <code>{'>2 && <5'}</code>, <code>{'<3 || >10'}</code>. Некоректний або суперечливий вираз підсвічується червоним.
        </div>
      )}

      <div
        ref={scrollRef}
        className={`publicstatistic-table-responsive ${capHeight ? 'publicstatistic-table-responsive--capped' : ''}`}
        style={{ cursor: 'grab', overflowX: 'auto', border: '1px solid #e5e7eb' }}
        {...dragListeners}
      >
        <table className="table publicstatistic-statistics-table" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr>
              <th style={thStyle} onClick={() => handleSort('kgnumber')}>
                № садочка <SortIcon column="kgnumber" sortConfig={sortConfig} />
              </th>
              {showDistrict && (
                <th style={thStyle} onClick={() => handleSort('district')}>
                  Район <SortIcon column="district" sortConfig={sortConfig} />
                </th>
              )}
              {numCols.map(({ key, label }) => (
                <th key={key} style={thStyle} onClick={() => handleSort(key)}>
                  {label} <SortIcon column={key} sortConfig={sortConfig} />
                </th>
              ))}
            </tr>
            {showFilters && (
              <tr style={{ background: '#f0f4ff' }}>
                <td style={{ padding: '4px 6px', minWidth: 120 }}>
                  <input
                    style={filterInputStyle}
                    value={columnFilters.kgnumber}
                    onChange={e => handleColumnFilter('kgnumber', e.target.value)}
                    placeholder="пошук…"
                  />
                </td>
                {showDistrict && (
                  <td style={{ padding: '4px 6px', minWidth: 130 }}>
                    <input
                      style={filterInputStyle}
                      value={columnFilters.district}
                      onChange={e => handleColumnFilter('district', e.target.value)}
                      placeholder="пошук…"
                    />
                  </td>
                )}
                {numCols.map(({ key }) => {
                  const check = columnFilters[key] ? validateFilter(columnFilters[key]) : { valid: true };
                  return (
                    <td key={key} style={{ padding: '4px 6px', minWidth: 82, verticalAlign: 'top' }}>
                      <input
                        style={{
                          ...filterInputStyle,
                          borderColor: !check.valid ? '#dc2626' : (columnFilters[key] ? '#0069ff' : '#c8d8ff'),
                          background: !check.valid ? '#fff5f5' : filterInputStyle.background,
                        }}
                        value={columnFilters[key]}
                        onChange={e => handleColumnFilter(key, e.target.value)}
                        placeholder=">0"
                      />
                      {columnFilters[key] && (
                        <div style={{ fontSize: '0.65rem', color: check.valid ? '#0069ff' : '#dc2626', marginTop: 2, lineHeight: 1.2 }}>
                          {describeFilter(columnFilters[key])}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            )}
            {filteredTotals && (
              <tr style={{ background: '#eef2ff', borderBottom: '2px solid #c7d2fe' }}>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'left', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>Разом:</strong></td>
                {showDistrict && <td></td>}
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.total.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.total3_6.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age0_1.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age1_2.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age2_3.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age3_4.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age4_5.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age5_6.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age6_7.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age7_8.toLocaleString()}</strong></td>
                <td style={{ padding: '8px 8px', fontWeight: 700, color: '#1e40af', textAlign: 'center', verticalAlign: 'middle', fontSize: '0.875rem' }}><strong>{filteredTotals.age8_9.toLocaleString()}</strong></td>
              </tr>
            )}
          </thead>
          <tbody>
            {processedData.map((row, index) => (
              <React.Fragment key={row.id}>
                <tr
                  className={index % 2 === 0 ? 'publicstatistic-row-even' : ''}
                  onClick={() => setOpenDetailId(prev => prev === row.id ? null : row.id)}
                  style={{
                    cursor: 'pointer',
                    borderLeft: openDetailId === row.id ? '3px solid #0069ff' : '3px solid transparent',
                    background: openDetailId === row.id ? '#f0f4ff' : undefined,
                  }}
                  title="Натисніть для перегляду розподілу дітей"
                >
                  <td className="publicstatistic-district-name">
                    <span style={{ marginRight: 4, fontSize: '0.7rem', color: openDetailId === row.id ? '#0069ff' : '#9ca3af' }}>
                      {openDetailId === row.id ? '▼' : '▶'}
                    </span>
                    {row.kgnumber}
                  </td>
                  {showDistrict && <td className="publicstatistic-district-name">{row.district}</td>}
                  <td>{row.total.toLocaleString()}</td>
                  <td>{row.total3_6.toLocaleString()}</td>
                  <td>{row.age0_1.toLocaleString()}</td>
                  <td>{row.age1_2.toLocaleString()}</td>
                  <td>{row.age2_3.toLocaleString()}</td>
                  <td>{row.age3_4.toLocaleString()}</td>
                  <td>{row.age4_5.toLocaleString()}</td>
                  <td>{row.age5_6.toLocaleString()}</td>
                  <td>{row.age6_7.toLocaleString()}</td>
                  <td>{row.age7_8.toLocaleString()}</td>
                  <td>{row.age8_9.toLocaleString()}</td>
                </tr>
                {openDetailId === row.id && !isMobile && (
                  <tr>
                    <td colSpan={1 + (showDistrict ? 1 : 0) + numCols.length} style={{ padding: 0, background: '#f8faff', borderLeft: '3px solid #0069ff' }}>
                      <ChildrenKindergartenDetail
                        kgId={row.id}
                        kgNumber={row.kgnumber}
                        district={row.district}
                        dataParams={dataParams}
                        disabilities={disabilities}
                        onClose={() => setOpenDetailId(null)}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {isMobile && (
        <MobileSheet
          isOpen={!!openDetailId}
          onClose={() => setOpenDetailId(null)}
        >
          {openDetailRow && (
            <ChildrenKindergartenDetail
              kgId={openDetailRow.id}
              kgNumber={openDetailRow.kgnumber}
              district={openDetailRow.district}
              dataParams={dataParams}
              disabilities={disabilities}
              onClose={() => setOpenDetailId(null)}
            />
          )}
        </MobileSheet>
      )}
    </div>
  );
};

export default ChildrenBreakdownTable;
